// MiloBoundariesPanel — Pass 6 / M11 + M17.
//
// Read-only surface that renders the canonical contracts declared in
// backend/system_rules.py via the /api/system-rules endpoint. No edits,
// no toggles. Single source of truth on the backend; this panel just
// renders what the API returns.
//
// Sections rendered, in order:
//   1. M18 — Operator/customer separation contract
//   2. M17 — System capability matrix (oscar vs milo)
//   3. M17 — Action tier vocabulary (L0 / L1 / L2 / L3)
//   4. M11 — Explicit L2 read-only contract (allowed / denied caps)

import React, { useEffect, useState } from 'react';
import { Shield, AlertCircle, CheckCircle2, XCircle, Lock, Database, Activity } from 'lucide-react';
import { getSystemRules, getMiloSeparationEvents } from '../../data/api';

// Reuse the visual tokens that AITab uses elsewhere so the panel feels
// native to the rest of the AI surface.
// Pass 8 / M21 — aligned to the canonical AI palette established in
// AITab.jsx + MiloConsolePanel.jsx. Prior Pass 6 values had drifted
// slightly (#C8A455 vs #C6A75A, #2A2419 vs #1B1812, etc.).
const GOLD    = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK    = '#1B1812';
const MUTED   = '#5C5C66';
const BORDER  = 'rgba(0,0,0,0.10)';
const SURFACE = '#FFFFFF';

// Corrective pre-apply pass — premium card / glassmorphic helpers used
// across the AI admin surface. White-main, gold-accent, black-minimal,
// no cream/beige. Used by both MiloBoundariesPanel and OscarGuidancePanel.
const aiCardStyle = {
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: 14,
  padding: 22,
  boxShadow: '0 1px 0 rgba(198,167,90,0.06) inset, 0 18px 36px -22px rgba(27,24,18,0.10)',
  position: 'relative',
};

const aiCardAccentStripe = {
  // Subtle gold accent on the top edge — futuristic without being loud.
  position: 'absolute',
  top: -1, left: 22, right: 22, height: 2,
  background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
  borderRadius: 2,
  opacity: 0.55,
};

const SectionHeader = ({ Icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16,
    paddingBottom: 14, borderBottom: `1px solid ${BORDER}` }}>
    {/* Refined: circle outline with thin gold border, white fill, gold icon.
        Replaces the prior flat gold-tinted square for a more luxe AI identity. */}
    <div style={{ width: 38, height: 38, borderRadius: '50%',
      background: SURFACE,
      border: `1.5px solid ${GOLD}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 0 0 4px rgba(198,167,90,0.06)' }}>
      <Icon size={17} color={GOLD_DK} strokeWidth={1.75} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
        fontSize: 19, fontWeight: 600, color: DARK,
        letterSpacing: '0.2px', lineHeight: 1.15 }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: MUTED, marginTop: 3,
          letterSpacing: '0.1px', lineHeight: 1.45 }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const palette = (status === 'live')
    ? { bg: 'rgba(34,197,94,0.12)', fg: '#15803d', br: 'rgba(34,197,94,0.30)', label: 'LIVE' }
    : (status === 'reserved_not_built')
    ? { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.15)', label: 'RESERVED' }
    : { bg: 'rgba(198,167,90,0.10)', fg: GOLD_DK, br: 'rgba(198,167,90,0.30)', label: status?.toUpperCase() || '—' };
  return (
    <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.6px',
      padding: '2px 8px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}` }}>
      {palette.label}
    </span>
  );
};

const MiloBoundariesPanel = () => {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(null);
  // Corrective pre-apply pass — separation events read surface.
  const [separationEvents, setSeparationEvents] = useState({ events: [], summary: {}, count: 0 });

  useEffect(() => {
    getSystemRules()
      .then(setRules)
      .catch(err => setError(err?.response?.data?.detail || err.message));
    // Best-effort — failure doesn't block panel render.
    getMiloSeparationEvents(20)
      .then(setSeparationEvents)
      .catch(() => {});
  }, []);

  if (error) {
    return (
      <div data-testid="milo-boundaries-error"
        style={{ padding: 20, color: '#b91c1c',
          background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.20)',
          borderRadius: 10, fontSize: 13 }}>
        <strong>Could not load boundaries:</strong> {error}
      </div>
    );
  }
  if (!rules) {
    return (
      <div data-testid="milo-boundaries-loading"
        style={{ padding: 20, color: MUTED, fontSize: 13, fontStyle: 'italic' }}>
        Loading boundaries…
      </div>
    );
  }

  const op = rules.operator_tier_contract || {};
  const contracts = rules.boundary_contracts || {};
  const tiers = rules.action_tiers || {};
  const l2 = rules.l2_read_only_contract || {};

  // Capability columns — derive from the union of all systems' keys
  // so the matrix renders correctly even if backend adds new caps.
  const allCapabilities = Array.from(new Set(
    Object.values(contracts).flatMap(c => Object.keys(c || {}))
  ));

  return (
    <div data-testid="milo-subtab-panel-boundaries"
      style={{ display: 'flex', flexDirection: 'column', gap: 28,
        padding: '4px 4px 24px 4px' }}>

      {/* ─── M18: Operator/customer separation contract ───────────── */}
      <section data-testid="boundaries-operator-tier" style={aiCardStyle}>
        <div style={aiCardAccentStripe} />
        <SectionHeader Icon={Lock} title="Operator / customer separation"
          subtitle="Milo answers admin questions only. Guest-facing requests are deflected to Oscar." />
        {/* Pass 6 (corrected) — show the live tier so operators can
            confirm Layer 2 is reading what they expect.
            Corrective pre-apply visual uplift: refined into a status pill
            with white surface + status-color left edge + small status dot. */}
        {rules.current_tier && (
          <div data-testid="boundaries-current-tier"
            style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 16px', borderRadius: 10, marginBottom: 16,
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderLeft: `3px solid ${rules.current_tier === 'operator'
                ? '#15803d' : '#b91c1c'}`,
              boxShadow: rules.current_tier === 'operator'
                ? '0 0 0 1px rgba(34,197,94,0.05) inset, 0 8px 20px -12px rgba(34,197,94,0.20)'
                : '0 0 0 1px rgba(239,68,68,0.05) inset, 0 8px 20px -12px rgba(239,68,68,0.20)' }}>
            {/* Status dot */}
            <span style={{ width: 8, height: 8, borderRadius: '50%',
              background: rules.current_tier === 'operator' ? '#15803d' : '#b91c1c',
              boxShadow: rules.current_tier === 'operator'
                ? '0 0 0 3px rgba(34,197,94,0.18)'
                : '0 0 0 3px rgba(239,68,68,0.18)',
              flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2,
              flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 800,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: MUTED }}>
                Currently operating as
              </span>
              <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 17, fontWeight: 600, lineHeight: 1.1,
                color: rules.current_tier === 'operator' ? '#15803d' : '#b91c1c',
                letterSpacing: '0.3px' }}>
                {rules.current_tier}
              </span>
            </div>
            {rules.current_tier_source && rules.current_tier_source !== 'explicit' && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.4px',
                padding: '4px 9px', borderRadius: 99,
                background: 'rgba(0,0,0,0.04)', color: MUTED,
                fontStyle: 'italic', textTransform: 'uppercase',
                border: `1px solid ${BORDER}` }}>
                {rules.current_tier_source.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        )}
        <div style={{ fontSize: 13, color: DARK, lineHeight: 1.6, marginBottom: 12 }}>
          {op.description}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
          gap: '8px 14px', fontSize: 12 }}>
          <div style={{ color: MUTED, fontWeight: 600 }}>Mechanism:</div>
          <div style={{ color: DARK }}>{op.current_mechanism}</div>
          <div style={{ color: MUTED, fontWeight: 600 }}>Telemetry:</div>
          <div style={{ color: DARK, fontFamily: 'Menlo, monospace', fontSize: 11 }}>
            {op.telemetry_collection}
          </div>
          <div style={{ color: MUTED, fontWeight: 600 }}>Deflection target:</div>
          <div style={{ color: DARK }}>{op.deflection_target}</div>
          <div style={{ color: MUTED, fontWeight: 600 }}>Tracked failure modes:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(op.fail_modes || []).map(fm => (
              <span key={fm} data-testid={`boundaries-fail-mode-${fm}`}
                style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px',
                  borderRadius: 4, background: 'rgba(0,0,0,0.05)',
                  color: DARK, fontFamily: 'Menlo, monospace' }}>
                {fm}
              </span>
            ))}
          </div>
        </div>
        {(op.future_hardening || []).length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${BORDER}` }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.10em',
              textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>
              Future hardening (not in scope today)
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: DARK, lineHeight: 1.7 }}>
              {op.future_hardening.map((h, i) => (<li key={i}>{h}</li>))}
            </ul>
          </div>
        )}
      </section>

      {/* ─── M17: System capability matrix ────────────────────────── */}
      <section data-testid="boundaries-capability-matrix" style={aiCardStyle}>
        <div style={aiCardAccentStripe} />
        <SectionHeader Icon={Shield} title="System capability matrix"
          subtitle="Allow-list shape — anything not listed is implicitly denied." />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: MUTED,
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.10em',
                  textTransform: 'uppercase' }}>System</th>
                {allCapabilities.map(cap => (
                  <th key={cap} style={{ padding: '8px 12px', color: MUTED,
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.10em',
                    textTransform: 'uppercase', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {cap.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(contracts).map(([sysName, caps]) => (
                <tr key={sysName} data-testid={`boundaries-row-${sysName}`}
                  style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700,
                    color: DARK, textTransform: 'capitalize' }}>
                    {sysName}
                  </td>
                  {allCapabilities.map(cap => {
                    const granted = !!(caps && caps[cap]);
                    return (
                      <td key={cap} data-testid={`boundaries-cell-${sysName}-${cap}`}
                        style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {granted ? (
                          <CheckCircle2 size={16} color="#15803d" />
                        ) : (
                          <XCircle size={16} color="#9ca3af" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─── M17: Action tier vocabulary ─────────────────────────── */}
      <section data-testid="boundaries-action-tiers" style={aiCardStyle}>
        <div style={aiCardAccentStripe} />
        <SectionHeader Icon={Database} title="Action tier vocabulary"
          subtitle="L0 / L1 are live. L2 is reserved (see below). L3 is execution-tier — not built." />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.entries(tiers).map(([tierKey, tier]) => (
            <div key={tierKey} data-testid={`boundaries-tier-${tierKey}`}
              style={{ padding: '12px 14px', borderRadius: 8,
                background: 'rgba(0,0,0,0.025)', border: `1px solid ${BORDER}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: GOLD_DK,
                  fontFamily: 'Menlo, monospace', minWidth: 28 }}>
                  {tierKey}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: DARK }}>
                  {tier.name}
                </span>
                <StatusPill status={tier.status} />
              </div>
              <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.55,
                marginLeft: 38, marginBottom: 4 }}>
                {tier.description}
              </div>
              {(tier.examples || []).length > 0 && (
                <div style={{ marginLeft: 38, display: 'flex', gap: 6,
                  flexWrap: 'wrap', marginTop: 4 }}>
                  {tier.examples.map(ex => (
                    <span key={ex} style={{ fontSize: 10, fontWeight: 700,
                      padding: '2px 7px', borderRadius: 4,
                      background: 'rgba(198,167,90,0.10)', color: GOLD_DK,
                      fontFamily: 'Menlo, monospace' }}>
                      {ex}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ─── M11: Explicit L2 read-only contract ──────────────────── */}
      <section data-testid="boundaries-l2-contract" style={{
        background: SURFACE, border: `1px solid ${BORDER}`,
        borderRadius: 12, padding: 18 }}>
        <SectionHeader Icon={AlertCircle} title="L2 read-only contract"
          subtitle="Explicit declaration BEFORE any L2 actions are implemented." />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontFamily: 'Menlo, monospace', fontSize: 13,
            fontWeight: 800, color: GOLD_DK }}>{l2.tier}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: DARK }}>{l2.name}</span>
          <StatusPill status={l2.status} />
          {l2.read_only && (
            <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px',
              borderRadius: 99, background: 'rgba(34,197,94,0.12)',
              color: '#15803d', border: '1px solid rgba(34,197,94,0.30)',
              letterSpacing: '0.6px' }}>
              READ-ONLY BY DEFINITION
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: DARK, lineHeight: 1.6, marginBottom: 14 }}>
          {l2.description}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div data-testid="boundaries-l2-allowed">
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.10em',
              textTransform: 'uppercase', color: '#15803d', marginBottom: 6 }}>
              Allowed
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: DARK,
              lineHeight: 1.7 }}>
              {(l2.allowed_capabilities || []).map(c => (
                <li key={c} style={{ fontFamily: 'Menlo, monospace' }}>{c}</li>
              ))}
            </ul>
          </div>
          <div data-testid="boundaries-l2-denied">
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.10em',
              textTransform: 'uppercase', color: '#b91c1c', marginBottom: 6 }}>
              Denied
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: DARK,
              lineHeight: 1.7 }}>
              {(l2.denied_capabilities || []).map(c => (
                <li key={c} style={{ fontFamily: 'Menlo, monospace' }}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${BORDER}`,
          fontSize: 11, color: MUTED, fontStyle: 'italic' }}>
          L2 actions in registry today: <strong style={{ color: DARK, fontStyle: 'normal' }}>
            {l2.registry_count_today ?? 0}
          </strong>
        </div>
      </section>

      {/* ─── Corrective pass — Recent separation events ─────────────── */}
      <section data-testid="boundaries-separation-events" style={aiCardStyle}>
        <div style={aiCardAccentStripe} />
        <SectionHeader Icon={Activity} title="Recent separation events"
          subtitle="Live log of operator-tier gate decisions. Layer 1 / Layer 2 fails captured for audit." />
        {separationEvents.count > 0 ? (
          <>
            {/* Summary band — counts by fail_reason */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8,
              marginBottom: 14 }}>
              {Object.entries(separationEvents.summary).map(([reason, count]) => (
                <span key={reason}
                  data-testid={`separation-summary-${reason}`}
                  style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
                    padding: '4px 10px', borderRadius: 99,
                    background: 'rgba(198,167,90,0.08)',
                    color: GOLD_DK,
                    border: '1px solid rgba(198,167,90,0.25)',
                    textTransform: 'uppercase' }}>
                  {reason.replace(/_/g, ' ')} · {count}
                </span>
              ))}
            </div>
            {/* Most recent events — capped at 5 visible */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {separationEvents.events.slice(0, 5).map(e => (
                <div key={e.id}
                  data-testid={`separation-event-${e.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 8,
                    background: 'rgba(0,0,0,0.025)',
                    border: `1px solid ${BORDER}`,
                    fontSize: 11 }}>
                  <span style={{ fontFamily: 'Menlo, monospace',
                    fontSize: 10, color: GOLD_DK, fontWeight: 700,
                    minWidth: 110 }}>
                    {e.fail_reason}
                  </span>
                  <span style={{ color: MUTED, flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap' }}>
                    {e.endpoint}
                  </span>
                  <span style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>
                    {(e.created_at || '').slice(0, 19).replace('T', ' ')}
                  </span>
                </div>
              ))}
            </div>
            {separationEvents.count > 5 && (
              <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic',
                marginTop: 8, textAlign: 'center' }}>
                Showing 5 of {separationEvents.count} recent events.
              </div>
            )}
          </>
        ) : (
          <div data-testid="separation-events-empty"
            style={{ padding: '14px 12px', borderRadius: 8,
              background: 'rgba(34,197,94,0.06)',
              border: '1px solid rgba(34,197,94,0.20)',
              fontSize: 12, color: '#15803d',
              display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle2 size={16} color="#15803d" />
            <span>No separation events recorded — every request to date has cleared both layers cleanly.</span>
          </div>
        )}
      </section>

    </div>
  );
};

export default MiloBoundariesPanel;
